import { useGameStore } from '@/state/gameStore';
import {
  AUGMENT_BRANCHES,
  augmentCost,
  type AugmentBranch,
  type AugmentBranchDef,
  type AugmentEffect,
} from '@/domain/augments';
import { Icon, type IconName } from '@/ui/components/Icon';
import './AugmentPanel.css';

/**
 * Augment-Tree UI — 5 horizontal lanes (branches) × 5 sequential nodes each.
 * Player taps the NEXT-unlocked node to invest CD. Locked nodes (later in
 * the lane) are dimmed; owned nodes glow in the branch theme color.
 *
 * The 25 nodes don't fit on a phone in one screen — vertical scroll with
 * each lane being its own card-row.
 */

const EFFECT_LABEL: Record<AugmentEffect, string> = {
  hp_pct: 'Max HP',
  def_pct: 'Verteidigung',
  atk_pct: 'Angriff',
  crit_chance: 'Crit-Chance',
  crit_mult: 'Crit-Schaden',
  loot_chance_pct: 'Drop-Chance',
  xp_pct: 'XP-Gewinn',
  extra_boss_roll: 'Boss-Loot-Würfe',
  special_charge_pct: 'Special-Ladung',
  skill_cd_pct: 'Skill-Cooldown',
  special_dmg_pct: 'Special-Schaden',
  special_start_charge: 'Special-Startladung',
  boost_duration_pct: 'Boost-Dauer',
  auto_farmer_yield_pct: 'Auto-Farmer-Output',
  salvage_refund_pct: 'Zerlege-Refund',
  offline_cap_hours: 'Offline-Cap (h)',
  auto_farmer_slots: 'Auto-Farmer-Slots',
};

function formatEffect(effect: AugmentEffect, value: number): string {
  const label = EFFECT_LABEL[effect];
  // Flat-count effects show the raw value; multiplier-style effects show %.
  if (effect === 'extra_boss_roll' || effect === 'auto_farmer_slots') {
    return `+${value} ${label}`;
  }
  if (effect === 'offline_cap_hours') {
    return `${label}: ${value} h`;
  }
  if (effect === 'crit_mult') {
    return `+${Math.round(value * 100)}% ${label}`;
  }
  if (effect === 'special_start_charge') {
    return `${label}: ${Math.round(value * 100)}%`;
  }
  // skill_cd_pct is stored as positive but reduces; display as negative %.
  if (effect === 'skill_cd_pct') {
    return `-${Math.round(value * 100)}% ${label}`;
  }
  return `+${Math.round(value * 100)}% ${label}`;
}

export function AugmentPanel() {
  const player = useGameStore((s) => s.player);
  const buy = useGameStore((s) => s.buyAugmentNode);

  if (!player) return null;
  const augments = player.augments ?? { survivor: 0, aggressor: 0, hunter: 0, engineer: 0, mystic: 0 };
  const cd = player.corruptedData ?? 0;

  const branches = (Object.keys(AUGMENT_BRANCHES) as AugmentBranch[]).map((k) => AUGMENT_BRANCHES[k]);

  return (
    <section className="augment-panel">
      <div className="augment-panel__header">
        <h2 className="augment-panel__title">Augment-Tree</h2>
        <div className="augment-panel__sub">5 Branches × 5 Nodes — sequenziell freischalten mit Corrupted Data</div>
      </div>

      {branches.map((branch) => (
        <BranchLane
          key={branch.id}
          branch={branch}
          owned={augments[branch.id] ?? 0}
          cd={cd}
          onBuy={() => buy(branch.id)}
        />
      ))}
    </section>
  );
}

interface LaneProps {
  branch: AugmentBranchDef;
  owned: number;
  cd: number;
  onBuy: () => void;
}

function BranchLane({ branch, owned, cd, onBuy }: LaneProps) {
  const iconName = branch.iconKey as IconName;
  const nextNode = branch.nodes[owned];     // undefined → branch maxed
  const nextCost = nextNode ? augmentCost(nextNode.position) : 0;
  const canAfford = cd >= nextCost;

  return (
    <div className="augment-lane card" style={{ '--branch-color': branch.themeColor } as React.CSSProperties}>
      <div className="augment-lane__header">
        <span className="augment-lane__icon"><Icon name={iconName} size={18} /></span>
        <span className="augment-lane__name">{branch.id.toUpperCase()}</span>
        <span className="augment-lane__progress">{owned} / 5</span>
      </div>
      <div className="augment-lane__nodes">
        {branch.nodes.map((node, i) => {
          const isOwned = i < owned;
          const isNext = i === owned;
          const isLocked = i > owned;
          const cost = augmentCost(node.position);
          return (
            <div
              key={node.position}
              className={`augment-node ${isOwned ? 'augment-node--owned' : ''} ${isNext ? 'augment-node--next' : ''} ${isLocked ? 'augment-node--locked' : ''}`}
            >
              <div className="augment-node__pos">{node.position}</div>
              <div className="augment-node__effects">
                {node.effects.map((eff, j) => (
                  <div key={j} className="augment-node__effect">{formatEffect(eff.effect, eff.value)}</div>
                ))}
              </div>
              {isNext && (
                <div className="augment-node__cost-row">
                  <span className="augment-node__cost">{cost.toLocaleString('de-DE')} ⌬</span>
                  <button
                    type="button"
                    className="augment-node__buy"
                    disabled={!canAfford}
                    onClick={onBuy}
                  >
                    Kaufen
                  </button>
                </div>
              )}
              {isOwned && <div className="augment-node__owned-tag">✓ Aktiv</div>}
              {isLocked && <div className="augment-node__locked-tag">Vorgänger fehlt</div>}
            </div>
          );
        })}
      </div>
      {!nextNode && (
        <div className="augment-lane__maxed">Branch komplett — alle 5 Nodes aktiv</div>
      )}
    </div>
  );
}
